import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

import { msal } from '@config/msalConfig';
import { AppRouter } from 'Router';
import { Header, RoleRestriction } from '@modules/core/components';
import { useStore } from '@store/StoreEffect';
import { RoleType, AvailableRoles } from '@modules/core/models';
import { AppInsights, HttpClient, RoleUtils } from '@modules/core/utils';
import ErrorPage from '@pages/error-page/ErrorPage';

import './assets/styles/index.scss';

export const App = observer(() => {
    const { settingsStore, userStore } = useStore();

    const [loaded, setLoaded] = useState<boolean>();
    const activeAccount = useAccount();
    const { instance, inProgress } = useMsal();
    const idTokenClaims = activeAccount?.idTokenClaims as Partial<{ roles: RoleType[] }>;
    const roles = (idTokenClaims?.roles || []).map((role: string) => AvailableRoles[role]);

    useEffect(() => {
        if (!loaded) {
            HttpClient.init();
            userStore.setAzureUser(activeAccount);
            userStore.loadCurrentUser();
            userStore.loadUserRoles();
            document.body.classList.add('theme-light');
            setLoaded(true);
        }
    }, [userStore, userStore.isUser]);

    useEffect(() => {
        const handleAuth = async () => {
            // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/errors.md#browserautherrors
            if (instance && inProgress === InteractionStatus.None && settingsStore.isMsalInteractionRequired) {
                AppInsights.trackException(null, { type: 'APP.tsx:AcquireTokenSilent:Interaction' });
                await instance.handleRedirectPromise();
                await instance.acquireTokenRedirect({ scopes: msal.scopes });
            }

            if (instance && inProgress === InteractionStatus.None && settingsStore.isReloginRequired) {
                AppInsights.trackException(null, { type: 'APP.tsx:AcquireTokenSilent:Relogin' });
                await instance.handleRedirectPromise();
                await instance.loginRedirect({ scopes: msal.scopes });
            }
        };

        handleAuth();
    }, [instance, inProgress, settingsStore.isMsalInteractionRequired, settingsStore.isReloginRequired]);

    if (!loaded) {
        return null;
    }

    if (RoleUtils.isBasicUser(roles)) {
        return (
            <main>
                <header>
                    <Header user={userStore.currentUser} userStore={userStore} showUserRole={false} />
                </header>
                <ErrorPage errorText="You do not have access to this portal." />
            </main>
        );
    }

    return (
        <RoleRestriction accessRoles={[RoleType.Superuser, RoleType.Support, RoleType.Admin]}>
            <AppRouter />
        </RoleRestriction>
    );
});
